import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Autocomplete, { AutocompleteRenderInputParams } from '@mui/material/Autocomplete';
import React, { useMemo, useCallback } from 'react';
import { ATTRIBUTE_ORDER, STUDY_KEY_TITLE } from '../constant';
import RadioGroup from '../RadioGroup';
import { Study, StudyAttribute, Indication } from '../types';
import type { StudyKeyConfig } from '../types';
import IndicationComparisonActions from './IndicationComparisonActions';
import IndicationComparisonTitle from './IndicationComparisonTitle';
import IndicationSummaryTable from './IndicationSummaryTable';
import StudyComparisonBlock from './StudyComparisonBlock';
import styles from './styles';

const EMPTY = '—';

function formatStudyValue(key: string, value: unknown): string {
  if (value == null || value === '') return EMPTY;
  const config = STUDY_KEY_TITLE[key];
  if (config?.options && typeof value === 'boolean') {
    const opt = config.options.find((o: string | { label: string; value: boolean }) =>
      typeof o === 'object' ? o.value === value : false
    );
    return (typeof opt === 'object' ? opt?.label : undefined) ?? String(value);
  }
  return String(value);
}

/* eslint-disable-next-line react/require-default-props */
interface EditableStudyKeyCellProps {
  fieldKey: string;
  value: unknown;
  config: StudyKeyConfig;
  studyId: string | number;
  // eslint-disable-next-line react/require-default-props, no-unused-vars
  onUpdateStudyField?: (key: string, value: string | number | boolean) => void;
}

const EditableStudyKeyCell = ({
  fieldKey,
  value,
  config,
  studyId,
  onUpdateStudyField
}: EditableStudyKeyCellProps): React.ReactElement => {
  const handleRadioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyField?.(fieldKey, e.target.value === 'true');
    },
    [fieldKey, onUpdateStudyField]
  );

  const handleAutocompleteChange = useCallback(
    (_e: React.SyntheticEvent, newValue: string) => {
      onUpdateStudyField?.(fieldKey, newValue ?? '');
    },
    [fieldKey, onUpdateStudyField]
  );

  const renderAutocompleteInput = useCallback(
    (params: AutocompleteRenderInputParams) => (
      <TextField
        id={params.id}
        fullWidth={params.fullWidth}
        size={params.size}
        type={config.inputType}
        placeholder={`Enter ${config.title}`}
        InputProps={{
          ...params.InputProps,
          inputProps: params.inputProps
        }}
        InputLabelProps={params.InputLabelProps}
        disabled={params.disabled}
      />
    ),
    [config.inputType, config.title]
  );

  const handleFieldChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyField?.(fieldKey, e.target.value);
    },
    [fieldKey, onUpdateStudyField]
  );

  if (config.inputType === 'radio-group' && config.options) {
    const options = config.options
      .filter((o): o is { label: string; value: boolean } => typeof o === 'object')
      .map(o => ({ label: o.label, value: String(o.value) }));
    return (
      <RadioGroup
        id={`edit-${studyId}-${fieldKey}`}
        value={value === true || value === false ? String(value) : ''}
        options={options}
        onChange={handleRadioChange}
      />
    );
  }

  if (config.inputType === 'text' || config.inputType === 'number') {
    const options = config.options || [];
    if (options.length > 0) {
      return (
        <Autocomplete
          freeSolo
          disableClearable
          size='small'
          fullWidth
          options={options}
          value={value ?? ''}
          onInputChange={handleAutocompleteChange}
          renderInput={renderAutocompleteInput}
        />
      );
    }
    return (
      <TextField
        size='small'
        fullWidth
        type={config.inputType}
        value={value ?? ''}
        onChange={handleFieldChange}
        placeholder={`Enter ${config.title}`}
      />
    );
  }

  return <>{formatStudyValue(fieldKey, value)}</>;
};

interface EditableAttributeCellProps {
  attrName: string;
  value: string;
  // eslint-disable-next-line react/require-default-props, no-unused-vars
  onUpdateStudyAttribute?: (attributeName: string, value: string) => void;
}

const EditableAttributeCell = ({
  attrName,
  value,
  onUpdateStudyAttribute
}: EditableAttributeCellProps) => {
  const handleAttributeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateStudyAttribute?.(attrName, e.target.value);
    },
    [attrName, onUpdateStudyAttribute]
  );
  return <TextField size='small' fullWidth value={value} onChange={handleAttributeChange} />;
};

function getAttributeValue(study: Study | null, attributeName: string): string {
  if (!study?.attributes) return EMPTY;
  const attr = study.attributes.find((a: StudyAttribute) => a.attribute_name === attributeName);
  const val = attr?.attribute_value;
  return val != null && val !== '' ? String(val) : EMPTY;
}

const STUDY_KEYS_ORDER = ATTRIBUTE_ORDER.filter((k: string) => STUDY_KEY_TITLE[k]);

function getStudyKey(
  study: Study | null,
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): string | number | null {
  if (!study) return null;
  const attrs = study.attributes ?? [];
  if (identifierAttributeId != null) {
    const identifierAttr = attrs.find(
      (a: StudyAttribute) => Number(a.attribute_id) === identifierAttributeId
    );
    const identifierVal = identifierAttr?.attribute_value;
    if (identifierVal != null && identifierVal !== '') {
      return String(identifierVal);
    }
  }
  if (nctIdAttributeId != null) {
    const nctAttr = attrs.find((a: StudyAttribute) => Number(a.attribute_id) === nctIdAttributeId);
    const nctVal = nctAttr?.attribute_value;
    if (nctVal != null && nctVal !== '') {
      return String(nctVal);
    }
  }
  return study.id ?? null;
}

function getAttributeNamesForStudy(prevStudy: Study | null, currStudy: Study | null): string[] {
  const names = new Set<string>();
  prevStudy?.attributes?.forEach((a: StudyAttribute) => names.add(a.attribute_name));
  currStudy?.attributes?.forEach((a: StudyAttribute) => names.add(a.attribute_name));
  return Array.from(names).sort();
}

function getStudySlotsForComparison(
  prevStudies: Study[],
  currStudies: Study[],
  identifierAttributeId: number | null | undefined,
  nctIdAttributeId: number | null | undefined
): (string | number)[] {
  const prevList = prevStudies ?? [];
  const currList = currStudies ?? [];
  const currentKeys = currList
    .map((s: Study) => getStudyKey(s, identifierAttributeId, nctIdAttributeId))
    .filter((k: string | number | null): k is string | number => k != null);
  const previousKeys = prevList
    .map((s: Study) => getStudyKey(s, identifierAttributeId, nctIdAttributeId))
    .filter((k: string | number | null): k is string | number => k != null);

  const previousKeySet = new Set(previousKeys);
  const previousUnique = Array.from(previousKeySet);
  const currentOnly = Array.from(
    new Set(currentKeys.filter((k: string | number) => !previousKeySet.has(k)))
  );
  const sortedPrevious = previousUnique.sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true })
  );
  const sortedCurrentOnly = currentOnly.sort((a, b) =>
    String(a).localeCompare(String(b), undefined, { numeric: true })
  );
  return [...sortedPrevious, ...sortedCurrentOnly];
}

/* eslint-disable react/require-default-props */
interface IndicationComparisonProps {
  title: string;
  onCopy?: () => void;
  onAcceptCurrent?: () => void;
  onCopyIndication?: () => void;
  // eslint-disable-next-line no-unused-vars
  onCopyStudy?: (prevStudy: Study) => void;
  parentIndication: Indication | null;
  currentIndication: Indication | null;
  nctIdAttributeId: number | null;
  identifierAttributeId: number | null;
  showActions?: boolean;
  isCopying?: boolean;
  copyingStudyId?: string | number | null;
  editingStudyId?: number | string | null;
  editingStudyData?: Study | null;
  // eslint-disable-next-line no-unused-vars
  onEditStudy?: (study: Study) => void;
  onSaveStudy?: () => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateStudyField?: (key: string, value: string | number | boolean) => void;
  // eslint-disable-next-line no-unused-vars
  onUpdateStudyAttribute?: (attributeName: string, value: string) => void;
}
/* eslint-enable react/require-default-props */

const IndicationComparison = ({
  title,
  parentIndication,
  currentIndication,
  nctIdAttributeId,
  identifierAttributeId,
  onCopy,
  onAcceptCurrent,
  onCopyIndication,
  onCopyStudy,
  showActions = true,
  isCopying = false,
  copyingStudyId = null,
  editingStudyId = null,
  editingStudyData = null,
  onEditStudy,
  onSaveStudy,
  onUpdateStudyField,
  onUpdateStudyAttribute
}: IndicationComparisonProps) => {
  const prevIndication = parentIndication ?? null;
  const currIndication = currentIndication ?? null;

  const prevStudies = prevIndication?.studies ?? [];
  const currStudies = currIndication?.studies ?? [];

  const studySlotKeys = useMemo(
    () =>
      getStudySlotsForComparison(prevStudies, currStudies, identifierAttributeId, nctIdAttributeId),
    [prevStudies, currStudies, identifierAttributeId, nctIdAttributeId]
  );

  const prevStudyByKey = useMemo(
    () =>
      new Map<string | number, Study>(
        prevStudies
          .map(
            (s: Study) =>
              [getStudyKey(s, identifierAttributeId, nctIdAttributeId), s] as [
                string | number | null,
                Study
              ]
          )
          .filter((entry): entry is [string | number, Study] => entry[0] != null)
      ),
    [prevStudies, identifierAttributeId, nctIdAttributeId]
  );

  const currStudyByKey = useMemo(
    () =>
      new Map<string | number, Study>(
        currStudies
          .map(
            (s: Study) =>
              [getStudyKey(s, identifierAttributeId, nctIdAttributeId), s] as [
                string | number | null,
                Study
              ]
          )
          .filter((entry): entry is [string | number, Study] => entry[0] != null)
      ),
    [currStudies, identifierAttributeId, nctIdAttributeId]
  );

  const handleCopyStudyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = (e.currentTarget as HTMLButtonElement).dataset.slotKey;
      if (raw == null) return;
      const prevStudy = prevStudyByKey.get(raw) ?? prevStudyByKey.get(Number(raw));
      if (prevStudy) onCopyStudy?.(prevStudy);
    },
    [prevStudyByKey, onCopyStudy]
  );

  const handleEditStudyClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      const raw = (e.currentTarget as HTMLButtonElement).dataset.slotKey;
      if (raw == null) return;
      const currStudy = currStudyByKey.get(raw) ?? currStudyByKey.get(Number(raw));
      if (currStudy) onEditStudy?.(currStudy);
    },
    [currStudyByKey, onEditStudy]
  );

  const renderCurrentCellForStudyKey = (
    key: string,
    currStudy: Study | null,
    studyId: string | number
  ) => {
    const isEditing =
      editingStudyId != null && currStudy?.id === editingStudyId && editingStudyData != null;
    const displayStudy = isEditing ? editingStudyData : currStudy;
    const value = displayStudy?.[key];

    if (!isEditing) {
      return formatStudyValue(key, value);
    }

    const config = STUDY_KEY_TITLE[key];
    if (!config) return formatStudyValue(key, value);

    return (
      <EditableStudyKeyCell
        fieldKey={key}
        value={value}
        config={config}
        studyId={studyId}
        onUpdateStudyField={onUpdateStudyField}
      />
    );
  };

  const renderCurrentCellForAttribute = (attrName: string, currStudy: Study | null) => {
    const isEditing =
      editingStudyId != null && currStudy?.id === editingStudyId && editingStudyData != null;
    if (!isEditing) {
      return getAttributeValue(currStudy, attrName);
    }
    const attr = editingStudyData?.attributes?.find(
      (a: StudyAttribute) => a.attribute_name === attrName
    );
    const valueStr = String(attr?.attribute_value ?? '');

    return (
      <EditableAttributeCell
        attrName={attrName}
        value={valueStr}
        onUpdateStudyAttribute={onUpdateStudyAttribute}
      />
    );
  };

  const hasAnyAction = Boolean(onCopy || onAcceptCurrent || (!currIndication && onCopyIndication));

  return (
    <Box height='100%' display='flex' flexDirection='column'>
      <IndicationComparisonTitle title={title} />

      <Box sx={styles.content}>
        <IndicationSummaryTable
          prevText={prevIndication?.text ?? null}
          currText={currIndication?.text ?? null}
          emptyLabel={EMPTY}
        />

        {studySlotKeys.map((slotKey: string | number, displayIndex: number) => {
          const prevStudy = prevStudyByKey.get(slotKey) ?? null;
          const currStudy = currStudyByKey.get(slotKey) ?? null;
          const canCopyStudy = currStudy == null && prevStudy != null && onCopyStudy != null;
          const studyCopying = prevStudy != null && copyingStudyId === prevStudy.id;
          const rowStudyId = currStudy?.id ?? slotKey;

          return (
            <StudyComparisonBlock
              key={`study-${slotKey}`}
              slotKey={slotKey}
              displayIndex={displayIndex}
              prevStudy={prevStudy}
              currStudy={currStudy}
              rowStudyId={rowStudyId}
              canCopyStudy={canCopyStudy}
              studyCopying={studyCopying}
              isCopying={isCopying}
              onCopyStudyClick={handleCopyStudyClick}
              onEditStudyClick={onEditStudy != null ? handleEditStudyClick : undefined}
              onSaveStudy={onSaveStudy}
              editingStudyId={editingStudyId ?? null}
              studyKeysOrder={STUDY_KEYS_ORDER}
              attributeNames={getAttributeNamesForStudy(prevStudy, currStudy)}
              formatStudyValue={formatStudyValue}
              getAttributeValue={getAttributeValue}
              renderCurrentCellForStudyKey={renderCurrentCellForStudyKey}
              renderCurrentCellForAttribute={renderCurrentCellForAttribute}
            />
          );
        })}
      </Box>

      <IndicationComparisonActions
        showActions={showActions}
        hasAnyAction={hasAnyAction}
        currIndication={currIndication}
        isCopying={isCopying}
        onCopyIndication={onCopyIndication}
        onCopy={onCopy}
        onAcceptCurrent={onAcceptCurrent}
      />
    </Box>
  );
};

export default React.memo(IndicationComparison);
